import type {
  Account,
  AiConnection,
  Asset,
  Brand,
  Business,
  HelperInvite,
  Onboarding,
  SetupStep,
} from './seed';
import type { Post } from '../types';

const MINIMUM_MATERIALS_FOR_SETUP = 3;

export type StepState = 'done' | 'later' | 'waiting' | 'todo';
export interface StepInfo {
  id: SetupStep;
  name: string;
  time: string;
  state: StepState;
  note?: string;
  variables?: Record<string, string>;
}

export function setupSteps(appState: {
  business: Business;
  brand: Brand;
  assets: Asset[];
  aiConnection: AiConnection;
  accounts: Account[];
  posts: Post[];
  onboarding: Onboarding;
  helpers: HelperInvite[];
}): StepInfo[] {
  const { business, brand, assets, aiConnection, accounts, posts, onboarding, helpers } = appState;
  const postponedForLater = (id: SetupStep) => onboarding.later.includes(id);
  const stepState = (id: SetupStep, done: boolean, waiting = false): StepState => {
    if (done) return 'done';
    if (waiting) return 'waiting';
    if (postponedForLater(id)) return 'later';
    return 'todo';
  };
  const hasOpeningHours = business.hours.some((dayHours) => !dayHours.closed && dayHours.open);
  const businessDone = !!(business.address && hasOpeningHours);
  const activeMaterialCount = assets.filter((asset) => !asset.archived).length;
  const pendingHelper = helpers.find((helper) => helper.status === 'Pending');
  const businessAccounts = accounts.filter((account) => !/personal/i.test(account.type));
  const brokenAccounts = businessAccounts.filter((account) => account.health !== 'Connected');
  const samplePosts = posts.filter((post) => onboarding.samples.includes(post.id));
  const reviewedSamples = samplePosts.filter(
    (post) => post.status !== 'Needs your review' && post.status !== 'AMY is working'
  );
  const aiConnected = aiConnection.status === 'connected';
  let aiNote: string | undefined;
  if (aiConnected) aiNote = 'Connected by {name}';
  else if (pendingHelper) aiNote = 'Waiting for {name}';
  const aiPersonName = aiConnected ? (aiConnection.connectedBy ?? '') : (pendingHelper?.name ?? '');
  return [
    {
      id: 'business',
      name: 'Business details',
      time: '3–5 min',
      state: stepState('business', businessDone),
      note: businessDone ? 'Updated by {name} on {date}' : undefined,
      variables: { name: business.updatedBy ?? '', date: business.updatedAt ?? '' },
    },
    {
      id: 'brand',
      name: 'Brand & style',
      time: '5–10 min',
      state: stepState('brand', brand.version >= 1),
      note: 'Brand kit version {v}',
      variables: { v: String(brand.version) },
    },
    {
      id: 'materials',
      name: 'Add your photos and videos',
      time: '5 min',
      state: stepState('materials', activeMaterialCount >= MINIMUM_MATERIALS_FOR_SETUP),
      note: '{n} items in Materials',
      variables: { n: String(activeMaterialCount) },
    },
    {
      id: 'ai',
      name: 'Connect AI',
      time: '2–4 min',
      state: stepState('ai', aiConnected, !!pendingHelper),
      note: aiNote,
      variables: { name: aiPersonName },
    },
    {
      id: 'social',
      name: 'Connect social accounts',
      time: '2–5 min each',
      state: stepState('social', businessAccounts.length > 0 && brokenAccounts.length === 0),
      note: brokenAccounts.length ? '{n} of {total} need attention' : '{total} accounts connected',
      variables: { n: String(brokenAccounts.length), total: String(businessAccounts.length) },
    },
    {
      id: 'samples',
      name: 'Approve sample posts',
      time: '3–5 min',
      state: stepState(
        'samples',
        samplePosts.length > 0 && reviewedSamples.length === samplePosts.length
      ),
      note: '{n} of {total} reviewed',
      variables: { n: String(reviewedSamples.length), total: String(samplePosts.length) },
    },
    {
      id: 'activate',
      name: 'Activate your plan',
      time: '1 min',
      state: stepState('activate', !!onboarding.activated),
      note: onboarding.activated ? 'Activated on {date}' : undefined,
      variables: { date: onboarding.activated ?? '' },
    },
  ];
}

export interface UsageRow {
  key: string;
  day: number;
  task: 'Draft' | 'Revision';
  title: string;
  inputTokens: number;
  outputTokens: number;
}
const DAYS_BEFORE_PUBLISHING_THAT_AMY_DRAFTS = 3;
const ESTIMATED_DRAFT_INPUT_TOKENS = 1800;
const ESTIMATED_DRAFT_OUTPUT_TOKENS = 600;
const ESTIMATED_REVISION_INPUT_TOKENS = 1200;
const ESTIMATED_REVISION_OUTPUT_TOKENS = 400;

export function usageRows(posts: Post[], today: number): UsageRow[] {
  return posts
    .flatMap((post) => {
      const draftDay = Math.max(
        1,
        Math.min(post.day - DAYS_BEFORE_PUBLISHING_THAT_AMY_DRAFTS, today)
      );
      const rows: UsageRow[] = [
        {
          key: `${post.id}:1`,
          day: draftDay,
          task: 'Draft',
          title: post.title,
          inputTokens: ESTIMATED_DRAFT_INPUT_TOKENS,
          outputTokens: ESTIMATED_DRAFT_OUTPUT_TOKENS,
        },
      ];
      for (let revision = 2; revision <= (post.revision ?? 1); revision++)
        rows.push({
          key: `${post.id}:${revision}`,
          day: Math.min(draftDay + revision - 1, today),
          task: 'Revision',
          title: post.title,
          inputTokens: ESTIMATED_REVISION_INPUT_TOKENS,
          outputTokens: ESTIMATED_REVISION_OUTPUT_TOKENS,
        });
      return rows;
    })
    .filter((row) => row.day <= today)
    .sort((first, second) => second.day - first.day || first.key.localeCompare(second.key));
}

export const MODEL_PRICES_PER_MILLION_TOKENS: Record<
  string,
  { inputPricePerMillionTokens: number; outputPricePerMillionTokens: number }
> = {
  'Small model (example)': { inputPricePerMillionTokens: 0.1, outputPricePerMillionTokens: 0.4 },
  'Mid-size model (example)': { inputPricePerMillionTokens: 0.4, outputPricePerMillionTokens: 1.6 },
  'Large model (example)': { inputPricePerMillionTokens: 2, outputPricePerMillionTokens: 8 },
};
